/* eslint-disable no-restricted-imports */
import { Img } from '@/components/blocks/MaterialImage';
import { RenderProductImg } from '@/components/blocks/RenderProductImg';
import { Stack, Typography } from '@mui/material';
import {
	OrderHistoryDetailsProductCustomTextDetailsSX,
	OrderHistoryDetailsProductNameSX,
	OrderHistoryDetailsProductTextDetails2SX,
	OrderHistoryDetailsProductTextDetailsSX,
	OrderHistoryDetailsProductTextDiscountSX,
	OrderHistoryDetailsProductTextValueSX,
} from '@/components/content/OrderHistoryDetails/styles/product';
import { OrderItemListRows } from '@/data/Content/OrderItemList';
import { calculateOrderItemSummary } from '@/data/Content/Cart';
import { formatPrice } from '@/utils/formatPrice';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { Linkable } from '@/components/blocks/Linkable';
import { RenderProductCode } from '@/components/blocks/RenderProductCode';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import {ProductCustomization} from '@/components/blocks/ProductCustomization';
import {useOrderItemCustom} from '@/utils/useOrderItemCustom';

interface OrderHistoryDetailsProductProps {
	item: OrderItemListRows[0];
}

export const OrderHistoryDetailsProduct: React.FC<OrderHistoryDetailsProductProps> = ({ item }) => {
	const { details, orderItemListRow } = item;
	const size = findProductAttribute(details.attributes, 'Talla');

	const { discount, price, priceWithoutOffer } = calculateOrderItemSummary({
		offerPrice: details.prices.offer || 0,
		displayPrice: details.prices.list || 0,
		totalAdjustment: orderItemListRow.totalAdjustment.totalAdjustment,
		orderItemPrice: orderItemListRow.price.orderItemPrice,
	});

	const isMobile = useIsMobile();
	const {isOrderItemCustom, extendedAttributes} = useOrderItemCustom(orderItemListRow.orderItem);

	return (
		<Stack
			direction={{ xs: 'column', md: 'row' }}
			spacing={{ xs: 1, md: 1 }}
			justifyContent={{ xs: undefined, md: 'space-between' }}
			alignItems={{ xs: undefined, md: 'center' }}
		>
			<Stack direction="row" alignItems={{ xs: undefined, md: 'center' }} spacing={1}>
				<RenderProductImg
					fullImageRaw={details.thumbnail}
					render={(src) => (
						<Linkable href={item?.details?.seo?.href}>
							<Img
								src={src}
								alt="Product"
								sx={{
									width: 90,
									height: 90,
								}}
							/>
						</Linkable>
					)}
				/>
				<Stack>
					<Typography sx={OrderHistoryDetailsProductNameSX}>{details.name}</Typography>
					<Stack direction="row" spacing={{ md: 2 }} flexWrap="wrap">
						<Typography sx={OrderHistoryDetailsProductTextDetails2SX}>
							{`Talla: ${size?.value}`}
						</Typography>
						<Typography sx={OrderHistoryDetailsProductTextDetails2SX}>{'Cantidad: 1'}</Typography>
						{!isMobile ? (
							<RenderProductCode
								code={details.partNumber}
								render={(_code) => (
									<Typography sx={OrderHistoryDetailsProductTextDetailsSX}>{_code}</Typography>
								)}
							/>
						) : null}
					</Stack>
					<ProductCustomization sx={OrderHistoryDetailsProductCustomTextDetailsSX} show={isOrderItemCustom} extendedAttributes={extendedAttributes}/>
					{isMobile ? (
						<RenderProductCode
							code={details.partNumber}
							render={(_code) => (
								<Typography sx={OrderHistoryDetailsProductTextDetailsSX}>{_code}</Typography>
							)}
						/>
					) : null}
				</Stack>
			</Stack>

			<Stack direction="row" spacing={{ xs: 2, md: 6 }}>
				<Stack>
					<Typography sx={OrderHistoryDetailsProductTextDetailsSX}>{'Precio unitario'}</Typography>
					<Typography sx={OrderHistoryDetailsProductTextValueSX}>
						{formatPrice('es-MX', 'MXN', priceWithoutOffer)}
					</Typography>
				</Stack>
				<Stack>
					<Typography sx={OrderHistoryDetailsProductTextDetailsSX}>{'Subtotal'}</Typography>
					<Typography sx={OrderHistoryDetailsProductTextValueSX}>
						{formatPrice('es-MX', 'MXN', priceWithoutOffer)}
					</Typography>
				</Stack>
				<Stack>
					<Typography sx={OrderHistoryDetailsProductTextDetailsSX}>{'Descuento'}</Typography>
					<Typography sx={OrderHistoryDetailsProductTextDiscountSX}>
						{formatPrice('es-MX', 'MXN', discount)}
					</Typography>
				</Stack>
				<Stack>
					<Typography sx={OrderHistoryDetailsProductTextDetailsSX}>{'Total'}</Typography>
					<Typography sx={OrderHistoryDetailsProductTextValueSX}>
						{formatPrice('es-MX', 'MXN', price)}
					</Typography>
				</Stack>
			</Stack>
		</Stack>
	);
};
